/**
 * In-app notification overlay — shows a themed slide-in notification when
 * the service worker sends a push while the app is focused. Richer than
 * the browser's default notification.
 */

import { useEffect, useState, useCallback } from 'react';
import { Bell, AlarmClock, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface InAppNotification {
  id: string;
  title: string;
  body?: string;
  url?: string;
  reminderId?: string;
  actionToken?: string;
}

export function NotificationOverlay() {
  const toast = useToast();
  const navigate = useNavigate();
  const [notification, setNotification] = useState<InAppNotification | null>(null);
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => setNotification(null), 300);
  }, []);

  // Listen for service worker messages
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type === 'NAVIGATE' && event.data.url) {
        navigate(event.data.url);
        return;
      }
    }

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleMessage);
  }, [navigate]);

  // Listen for push events while focused (via BroadcastChannel)
  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;
    const channel = new BroadcastChannel('dossier-notifications');

    channel.addEventListener('message', (event) => {
      const data = event.data;
      if (data?.type === 'push-received') {
        setNotification({
          id: Date.now().toString(),
          title: data.title,
          body: data.body,
          url: data.url,
          reminderId: data.reminderId,
          actionToken: data.actionToken,
        });
        setVisible(true);

        // Auto-dismiss after 15 seconds
        setTimeout(() => {
          setVisible(false);
          setTimeout(() => setNotification(null), 300);
        }, 15000);
      }
    });

    return () => channel.close();
  }, []);

  async function handleSnooze() {
    if (!notification?.reminderId) return;
    try {
      await apiPost(`/reminders/${notification.reminderId}/snooze`, { minutes: 15 });
      toast.success('Snoozed for 15 minutes');
    } catch { /* ignore */ }
    dismiss();
  }

  async function handleDismissReminder() {
    if (!notification?.reminderId) return;
    try {
      await apiPost(`/reminders/${notification.reminderId}/dismiss`, {});
    } catch { /* ignore */ }
    dismiss();
  }

  if (!notification) return null;

  return (
    <div
      className={`fixed top-4 right-4 z-[90] w-80 bg-bg-surface border border-border rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
      }`}
    >
      <div className="flex items-start gap-3 p-4">
        <Bell size={18} className="text-accent flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary">{notification.title}</p>
          {notification.body && (
            <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{notification.body}</p>
          )}
        </div>
        <button onClick={dismiss} className="p-0.5 text-text-muted hover:text-text-primary flex-shrink-0">
          <X size={14} />
        </button>
      </div>
      <div className="flex items-center justify-between px-4 pb-3">
        <div className="flex gap-2">
          {notification.reminderId && (
            <>
              <button
                onClick={handleSnooze}
                className="flex items-center gap-1 px-2 py-1 text-xs text-text-secondary hover:text-warning hover:bg-bg-hover rounded transition-colors"
              >
                <AlarmClock size={12} /> Snooze
              </button>
              <button
                onClick={handleDismissReminder}
                className="flex items-center gap-1 px-2 py-1 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded transition-colors"
              >
                Dismiss
              </button>
            </>
          )}
        </div>
        {notification.url && (
          <button
            onClick={() => { navigate(notification.url!); dismiss(); }}
            className="flex items-center gap-0.5 px-2 py-1 text-xs text-accent hover:text-accent-hover transition-colors"
          >
            Open <ChevronRight size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
